import { IContactModel } from '../models/common';
import { BehaviorSubject, Observable } from 'rxjs';

export class FiUniqueContactsHelper {
    private contacts: IContactModel[] = [];
    private contactDuplicatesUids: number[][] = [];

    private readonly getContactValueForValidation: (contactValue: string, extendedValue: string) => string;

    constructor(
        contacts: IContactModel[],
        getContactValueForValidationFn: (contactValue: string, extendedValue: string) => string,
    ) {
        this.setContacts(contacts);
        this.getContactValueForValidation = getContactValueForValidationFn;
    }

    setContacts(contacts: IContactModel[]) {
        this.contacts = [...contacts];
    }

    setDuplicatesOnContactChange(changedContact: IContactModel) {
        const existingContactDuplicateUidIndex: number = this.contactDuplicatesUids.findIndex(
            (duplicateUids: number[]) => {
                return duplicateUids?.includes(changedContact.Uid);
            },
        );

        if (existingContactDuplicateUidIndex !== -1) {
            this.contactDuplicatesUids[existingContactDuplicateUidIndex].splice(existingContactDuplicateUidIndex, 1);
            if (this.contactDuplicatesUids[existingContactDuplicateUidIndex].length === 1) {
                this.contactDuplicatesUids.splice(existingContactDuplicateUidIndex, 1);
            }
        }

        if (!this.contacts.find((contact: IContactModel) => contact.Uid === changedContact.Uid)) {
            const relatedDuplicationIndex: number = this.contactDuplicatesUids.findIndex((duplicateUids: number[]) => {
                return duplicateUids.includes(changedContact.Uid);
            });

            if (relatedDuplicationIndex !== -1) {
                const removedUidIndex: number = this.contactDuplicatesUids[relatedDuplicationIndex].indexOf(
                    changedContact.Uid,
                );
                this.contactDuplicatesUids[relatedDuplicationIndex].splice(removedUidIndex, 1);
            }

            this._triggerDuplicationContactsChange();
            return;
        }

        const currentDuplicateUids: number[] = this.contacts
            .filter((contact: IContactModel) => this.isEqualContactValuesWithCurrent(changedContact, contact))
            .map((contact: IContactModel) => contact.Uid);

        if (!currentDuplicateUids.length) {
            this._triggerDuplicationContactsChange();
            return;
        }

        const relatedDuplicationIndex: number = this.contactDuplicatesUids.findIndex((duplicateUids: number[]) => {
            return duplicateUids.some((duplicateUid: number) => duplicateUid === currentDuplicateUids[0]);
        });

        if (relatedDuplicationIndex !== -1) {
            this.contactDuplicatesUids[relatedDuplicationIndex].splice(0, 0, changedContact.Uid);
        } else {
            currentDuplicateUids.splice(0, 0, changedContact.Uid);
            this.contactDuplicatesUids.push(currentDuplicateUids);
        }
        this._triggerDuplicationContactsChange();
    }

    private _duplicatedContactUidsWithError$ = new BehaviorSubject<number[]>([]);
    private _triggerDuplicationContactsChange() {
        const newDuplicatedContacts: number[] = this.contactDuplicatesUids.map((duplicateUids: number[]) => {
            return duplicateUids[0];
        });
        this._duplicatedContactUidsWithError$.next(newDuplicatedContacts);
    }

    get onDuplicationContactChange$(): Observable<number[]> {
        return this._duplicatedContactUidsWithError$.asObservable();
    }

    private isEqualContactValuesWithCurrent(contact: IContactModel, compareContact: IContactModel): boolean {
        const existingContactValue: string = this.getContactValueForValidation(
            contact.ContactValue,
            contact.ExtendedValue,
        );
        const currentContactValue: string = this.getContactValueForValidation(
            compareContact.ContactValue,
            compareContact.ExtendedValue,
        );
        return existingContactValue === currentContactValue && contact.Uid !== compareContact.Uid;
    }
}
